/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useMemo } from 'react';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import RouteErrorBoundary from '../../components/error/RouteErrorBoundary';
import { useDispatch } from 'react-redux';
import { AppRouteInterface, RouteInterface, RoutesInterface } from '../../types/routes';
import { getFirstInitiativeGroupId } from '../../selectors/routes';
import { AppLayout } from '../../layout/AppLayout';
import { portfolioTrackerInitiativeRoutes } from './PortfolioTrackerRoute';
import Header from '../../components/header';
import Loader from '../../components/loader';
import UTrModal from '../../components/utr-modal';
import { generateUrl } from '../util';
import { AppHeader } from '../../components/header/AppHeader';
import Dashboard, { DashboardSection } from '../../components/dashboard';
import { useAppSelector } from '../../reducers';
import { loadPortfolioTree } from '../../slice/portfolioTreeSlice';
import { loadPortfolioById } from '../../slice/portfolioSlice';
import { PortfolioNavigationMapRoute } from './PortfolioNavigationMapRoute';
import PTLogo from '../../images/Portfolio_Tracker_logo.svg';
import { getCurrentPortfolioTree, isPortfolioTrackerExchange } from '../../selectors/portfolio';
import { portfolioTrackerRouteMatcher } from '../appRootMatcher';

// Do not require initiative
export const portfolioTrackerRouteEntry: RouteInterface = {
  id: 'portfolio_tracker_root',
  label: 'Portfolio Tracker',
  tooltip: 'Portfolio Tracker',
  path: '/portfolio-tracker',
  icon: 'fa-folder-open',
  component: PortfolioTracker,
  appPermissionId: 'app_portfolio_tracker',
  exact: false,
  auth: true,
};

export const portfolioTrackerMapRoute: RouteInterface = {
  id: 'portfolio_tracker_map',
  component: PortfolioNavigationMapRoute,
  label: 'Organisational structure',
  path: `${portfolioTrackerRouteEntry.path}/initiative-structure/:portfolioId?`,
  icon: 'fa-folder-tree',
  tooltip: 'Navigation Tree of the Reporting Level Dashboards',
  appPermissionId: 'app_portfolio_tracker',
  auth: true,
};

export const portfolioTrackerRoutes: RoutesInterface = {
  PORTFOLIO_TRACKER: portfolioTrackerRouteEntry,
  PORTFOLIO_TRACKER_MAP: portfolioTrackerMapRoute,
  ...portfolioTrackerInitiativeRoutes,
}

const getPortfolioTrackerAppRoute = (isExchangeView = false): AppRouteInterface => ({
  appName: 'Portfolio Tracker',
  appIcon: PTLogo,
  routes: [
    portfolioTrackerRoutes.PORTFOLIO_TRACKER_PORTFOLIO,
    portfolioTrackerRoutes.PORTFOLIO_TRACKER_INSIGHTS,
    ...(isExchangeView
      ? [
          portfolioTrackerRoutes.PORTFOLIO_TRACKER_COMPANY_SETTINGS,
          portfolioTrackerRoutes.PORTFOLIO_TRACKER_BENCHMARKING,
        ]
      : []),
    portfolioTrackerRoutes.PORTFOLIO_TRACKER_ADMIN_SETTINGS,
    portfolioTrackerRoutes.PORTFOLIO_TRACKER_MANAGE_COMPANIES,
    portfolioTrackerRoutes.PORTFOLIO_TRACKER_MANAGE_USERS,
    portfolioTrackerRoutes.PORTFOLIO_TRACKER_CUSTOM_METRICS,
    portfolioTrackerRoutes.PORTFOLIO_TRACKER_INSIGHT_DASHBOARDS
  ],
  hasReportingLevels: true,
});

const useDecideInitiativeId = (portfolioId: string) => {

  const firstInitiativeId = useAppSelector(getFirstInitiativeGroupId);
  return useMemo(() => {
    if (portfolioId !== '' || !firstInitiativeId) {
      return;
    }
    return generateUrl(portfolioTrackerRoutes.PORTFOLIO_TRACKER_PORTFOLIO, { portfolioId: firstInitiativeId });
  }, [firstInitiativeId, portfolioId])
}

/**
 * Portfolio Tracker App entry point
 */
export function PortfolioTracker() {

  const portfolioIdpath = '/:_?/:_?/:portfolioId?';
  const history = useHistory();
  const dispatch = useDispatch();

  const match = useRouteMatch<{ portfolioId: string }>({ path: portfolioIdpath });
  const portfolioId = match?.params?.portfolioId ?? '';

  const initiativeTree = useAppSelector(getCurrentPortfolioTree);
  const portfolioTree = useAppSelector((state) => state.portfolioTree);
  const portfolioState = useAppSelector((state) => state.portfolio);
  const isExchangeView = useAppSelector(isPortfolioTrackerExchange);

  // Redirect if initiativeId is available initiative tree, but missing in url
  const isRoutePath = useRouteMatch({ path: portfolioTrackerRouteEntry.path, exact: true });
  const redirectUrl = useDecideInitiativeId(portfolioId);

  React.useEffect(() => {
    if (isRoutePath && redirectUrl) {
      history.push(redirectUrl);
    }
  }, [history, isRoutePath, redirectUrl]);

  React.useEffect(() => {
    dispatch(loadPortfolioTree())
  }, [dispatch]);

  React.useEffect(() => {
    if (portfolioId) {
      dispatch(loadPortfolioById(portfolioId))
    }
  }, [dispatch, portfolioId]);

  if (!portfolioTree.loaded) {
    return <Loader />;
  }

  if (portfolioId && !portfolioState.loaded) {
    return <Loader />;
  }

  // "/:root(portfolio-tracker)",
  return (
    <RouteErrorBoundary>
        <AppLayout Header={
        <Route path={portfolioIdpath}>
            <Header
              displayRootSwitcher
              routeMatcher={portfolioTrackerRouteMatcher}
              rootInitiatives={portfolioTree.data}
            >
            <AppHeader
              mapRoute={portfolioTrackerMapRoute}
              initiativeTree={initiativeTree}
              appRoute={getPortfolioTrackerAppRoute(isExchangeView)}
              route={portfolioTrackerRoutes.PORTFOLIO_TRACKER_PORTFOLIO} />
          </Header>
        </Route>
      }>
        <Switch>
          <Route {...portfolioTrackerMapRoute} />
          {/*"/:root(portfolio-tracker)/portfolio/:portfolioId?",*/}
          {Object.entries(portfolioTrackerInitiativeRoutes).map(([k, route]) => {
            return <Route
              key={`route_${route.id}`}
              {...route}
              exact={route.exact ?? false} />
          })}
          <Route>
            {redirectUrl ? <Loader /> : (
              <Dashboard>
                <DashboardSection icon={'fa-folder-open'} title='Portfolio Tracker'>
                  <p>You currently don't have any Portfolio Trackers associated with this account.</p>
                  <p>Please contact our customer success team to set up your account.</p>
                </DashboardSection>
              </Dashboard>
            )}
          </Route>
        </Switch>
        <UTrModal />
      </AppLayout>
    </RouteErrorBoundary>
  )
}

export default PortfolioTracker;
